import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FormattedMessage, useIntl } from "react-intl";
import { MoreHorizontal } from "lucide-react";

import { cn } from "@/lib/utils";

export function PostPreviewOverflowMenu({
  size = "large",
  downloading,
  rejecting = false,
  onDownload,
  onReject,
  triggerClassName,
  iconClassName,
}: {
  size?: "compact" | "large" | "overlay";
  downloading: boolean;
  rejecting?: boolean;
  onDownload: () => void;
  onReject?: () => void;
  triggerClassName?: string;
  iconClassName?: string;
}) {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<{ top: number; right: number } | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuClassName =
    size === "compact"
      ? "w-36 max-w-[calc(100vw-1rem)] rounded-lg p-1 shadow-md"
      : size === "overlay"
        ? "w-72 max-w-[calc(100vw-1rem)] rounded-2xl p-2 shadow-2xl"
        : "w-48 max-w-[calc(100vw-1rem)] rounded-xl p-1.5 shadow-xl";
  const itemClassName =
    size === "compact"
      ? "min-h-8 rounded-md px-2.5 py-1.5 text-xs font-semibold"
      : size === "overlay"
        ? "min-h-12 rounded-xl px-4 py-3 text-base font-semibold"
        : "min-h-10 rounded-lg px-3 py-2 text-sm font-semibold";

  useEffect(() => {
    if (!open) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (
        menuRef.current?.contains(event.target as Node) ||
        triggerRef.current?.contains(event.target as Node)
      ) {
        return;
      }

      setOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    const updatePosition = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;

      setMenuPosition({
        top: rect.bottom + 8,
        right: Math.max(8, window.innerWidth - rect.right),
      });
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open) {
      setMenuPosition(null);
      return;
    }

    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;

    setMenuPosition({
      top: rect.bottom + 8,
      right: Math.max(8, window.innerWidth - rect.right),
    });
  }, [open]);

  const menu =
    open && menuPosition
      ? createPortal(
          <div
            ref={menuRef}
            className={cn(
              "fixed z-[80] overflow-hidden border border-border bg-popover",
              menuClassName,
            )}
            style={{ top: menuPosition.top, right: menuPosition.right }}
          >
            <button
              type="button"
              className={cn(
                "flex w-full items-center text-left text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50",
                itemClassName,
              )}
              onClick={() => {
                setOpen(false);
                onDownload();
              }}
              disabled={downloading}
            >
              <FormattedMessage id="approvals.downloadImage" defaultMessage="Download image" />
            </button>
            {onReject ? (
              <button
                type="button"
                className={cn(
                  "flex w-full items-center text-left text-destructive transition-colors hover:bg-destructive/10 disabled:cursor-not-allowed disabled:opacity-50",
                  itemClassName,
                )}
                onClick={() => {
                  setOpen(false);
                  onReject();
                }}
                disabled={rejecting}
              >
                <FormattedMessage id="common.delete" defaultMessage="Delete" />
              </button>
            ) : null}
          </div>,
          document.body,
        )
      : null;

  return (
    <div className="relative shrink-0">
      <button
        ref={triggerRef}
        type="button"
        className={cn(
          "flex h-11 w-11 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted/70 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50",
          triggerClassName,
        )}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={intl.formatMessage({
          id: "instagramPostChrome.postMenuPreview",
          defaultMessage: "Instagram post menu preview",
        })}
        onClick={() => setOpen((current) => !current)}
        disabled={downloading || rejecting}
      >
        <MoreHorizontal className={cn("h-5 w-5", iconClassName)} aria-hidden={true} />
      </button>
      {menu}
    </div>
  );
}
